import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { pdfService } from '@/services/pdfService';
import { submitSubmittal } from '@/services/hubspotService';
import type { SelectedDocument, ProjectFormData } from '@/types';
import { cn } from '@/utils';

interface PacketGenerationProps {
  selectedDocuments: SelectedDocument[];
  onPrevious: () => void;
  onNext: () => void;
  isGenerating: boolean;
  onSetGenerating: (isGenerating: boolean) => void;
  formData: Partial<ProjectFormData>;
}

const PacketGeneration = ({
  selectedDocuments,
  onPrevious,
  onNext,
  isGenerating,
  onSetGenerating,
  formData
}: PacketGenerationProps) => {
  const [generationType, setGenerationType] = useState<'preview' | 'download' | null>(null);

  const [previewData, setPreviewData] = useState<Uint8Array | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const sortedDocs = selectedDocuments.filter(doc => doc.selected).sort((a, b) => a.order - b.order);

  // Lazy-load PDF.js from CDN for inline preview rendering
  const ensurePdfJs = async () => {
    if ((window as any).pdfjsLib) return;
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    const worker = document.createElement('script');
    worker.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    worker.async = true;

    await Promise.all([
      new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      }),
      new Promise((resolve, reject) => {
        worker.onload = resolve;
        worker.onerror = reject;
        document.head.appendChild(worker);
      })
    ]);
    (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  };

  // Render preview into inline container when data is ready
  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      if (!isPreviewVisible || !previewData) return;
      try {
        await ensurePdfJs();
        if (cancelled) return;
        const pdfjsLib = (window as any).pdfjsLib;
        const container = previewContainerRef.current;
        if (!container) return;
        container.innerHTML = '';
        const loadingTask = pdfjsLib.getDocument({ data: previewData });
        const pdf = await loadingTask.promise;
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.1 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) continue;
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.background = '#fff';
          canvas.style.boxShadow = '0 10px 30px rgba(0,0,0,0.35)';
          canvas.style.borderRadius = '6px';
          container.appendChild(canvas);
          await page.render({ canvasContext: context, viewport }).promise;
        }
      } catch (err) {
        console.error('Failed to render inline preview', err);
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [isPreviewVisible, previewData]);

  // Helper function to map selected documents to submittalType checkboxes
  const getSubmittalTypeFromDocuments = () => {
    const submittalType = {
      tds: false,
      threePartSpecs: false,
      testReportIccEsr5194: false,
      testReportIccEsr5192: false,
      testReportIccEsl1645: false,
      fireAssembly: false,
      fireAssembly01: false,
      fireAssembly02: false,
      fireAssembly03: false,
      fireAssembly04: false,
      fireAssembly05: false,
      fireAssembly06: false,
      fireAssembly07: false,
      fireAssembly08: false,
      fireAssembly09: false,
      msds: false,
      leedGuide: false,
      installationGuide: false,
      warranty: false,
      samples: false,
      other: false,
    };

    // Populate submittalType based on selected documents
    sortedDocs.forEach(doc => {
      const docName = doc.document.name.toLowerCase();
      const docType = doc.document.type.toLowerCase();
      
      if (docName.includes('technical data sheet') || docType === 'tds') {
        submittalType.tds = true;
      }
      if (docName.includes('3-part spec') || docType === 'partspec') {
        submittalType.threePartSpecs = true;
      }
      if (docName.includes('esr-5194') || docName.includes('esr 5194')) {
        submittalType.testReportIccEsr5194 = true;
      }
      if (docName.includes('esr-5192') || docName.includes('esr 5192')) {
        submittalType.testReportIccEsr5192 = true;
      }
      if (docName.includes('esl-1645') || docName.includes('esl 1645') || docName.includes('acoustical')) {
        submittalType.testReportIccEsl1645 = true;
      }
      if (docName.includes('fire assembly 01')) {
        submittalType.fireAssembly = true;
        submittalType.fireAssembly01 = true;
      }
      if (docName.includes('fire assembly 02')) {
        submittalType.fireAssembly = true;
        submittalType.fireAssembly02 = true;
      }
      if (docName.includes('fire assembly 03')) {
        submittalType.fireAssembly = true;
        submittalType.fireAssembly03 = true;
      }
      if (docName.includes('fire assembly 04')) {
        submittalType.fireAssembly = true;
        submittalType.fireAssembly04 = true;
      }
      if (docName.includes('fire assembly 05')) {
        submittalType.fireAssembly = true;
        submittalType.fireAssembly05 = true;
      }
      if (docName.includes('fire assembly 06')) {
        submittalType.fireAssembly = true;
        submittalType.fireAssembly06 = true;
      }
      if (docName.includes('fire assembly 07')) {
        submittalType.fireAssembly = true;
        submittalType.fireAssembly07 = true;
      }
      if (docName.includes('fire assembly 08')) {
        submittalType.fireAssembly = true;
        submittalType.fireAssembly08 = true;
      }
      if (docName.includes('fire assembly 09')) {
        submittalType.fireAssembly = true;
        submittalType.fireAssembly09 = true;
      }
      if (docName.includes('msds') || docName.includes('material safety') || docType === 'msds') {
        submittalType.msds = true;
      }
      if (docName.includes('leed') || docType === 'leed') {
        submittalType.leedGuide = true;
      }
      if (docName.includes('installation') || docType === 'installation') {
        submittalType.installationGuide = true;
      }
      if (docName.includes('warranty') || docType === 'warranty') {
        submittalType.warranty = true;
      }
    });

    return submittalType;
  };

  const preparedFormData = {
    ...formData,
    emailAddress: formData.emailAddress || '',
    phoneNumber: formData.phoneNumber || '',
    productSize: formData.productSize || '3/4-in (20mm)',
    status: formData.status || {
      forReview: false,
      forApproval: false,
      forRecord: false,
      forInformationOnly: false,
    },
    submittalType: getSubmittalTypeFromDocuments(),
    date: formData.date || new Date().toLocaleDateString(),
  };

  const handleGenerate = async () => {
    console.log('Generate Packet button clicked');
    if (isGenerating) return;
    setGenerationType('download');
    onSetGenerating(true);
    try {
      console.log('Prepared form data for PDF:', preparedFormData); // Debug output
      const pdfBytes = await pdfService.generatePacket(preparedFormData, sortedDocs);
      await pdfService.downloadPacket(preparedFormData, sortedDocs);
      pdfService.downloadPDF(pdfBytes, `${formData.projectName || 'Untitled'}_Packet.pdf`);
      
      // Submit to HubSpot (non-blocking - fire and forget)
      // PDF generation already succeeded, so HubSpot submission won't affect user experience
      submitSubmittal(preparedFormData, sortedDocs).catch((error) => {
        // Error already logged in submitSubmittal, just catch to prevent unhandled rejection
        console.error('HubSpot submission error (non-blocking):', error);
      });
      
      onNext();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF packet.');
    } finally {
      setGenerationType(null);
      onSetGenerating(false);
    }
  };

  const handlePreview = async () => {
    console.log('Preview Packet button clicked');
    if (isGenerating) return;
    setGenerationType('preview');
    onSetGenerating(true);
    try {
      console.log('Prepared form data for preview:', preparedFormData); // Debug output
      const pdfBytes = await pdfService.generatePacket(preparedFormData, sortedDocs);
      setPreviewData(pdfBytes);
      setIsPreviewVisible(true);
    } catch (error) {
      console.error('Error generating PDF for preview:', error);
      alert('Failed to generate PDF preview.');
    } finally {
      setGenerationType(null);
      onSetGenerating(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-6xl mx-auto">
      <div className="card p-8 lg:p-12">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-3">Generate Submittal Packet</h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mt-2">Review your selections and generate your submittal packet with cover page and all selected documents.</p>
        </div>

        {/* Statistics Section */}
        <div className="max-w-md mx-auto mb-8">
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4 text-center border border-primary-200 dark:border-primary-800">
            <p className="text-primary-700 dark:text-primary-300 font-medium text-2xl">{sortedDocs.length}</p>
            <p className="text-sm text-primary-600 dark:text-primary-400">Documents Selected</p>
          </div>
        </div>

        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Submittal Packet Summary</h3>
          <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Project Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-gray-700 dark:text-gray-300 mb-6">
              <p><span className="font-medium">Project:</span> {formData.projectName || 'N/A'}</p>
              <p><span className="font-medium">Company:</span> {formData.submittedTo || 'N/A'}</p>
              <p><span className="font-medium">Prepared by:</span> {formData.preparedBy || 'N/A'}</p>
              <p><span className="font-medium">Email:</span> {formData.emailAddress || 'N/A'}</p>
              <p><span className="font-medium">Phone:</span> {formData.phoneNumber || 'N/A'}</p>
              <p><span className="font-medium">Project Number:</span> {formData.projectNumber || 'N/A'}</p>
              <p><span className="font-medium">Date:</span> {formData.date || new Date().toLocaleDateString()}</p>
              <p><span className="font-medium">Status:</span> {formData.status ? Object.entries(formData.status).filter(([_, v]) => v).map(([k]) => k.replace(/([A-Z])/g, ' $1').trim()).join(', ') : 'N/A'}</p>
            </div>
            
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Documents ({sortedDocs.length + 1})</h4>
              <div className="space-y-1">
                <p className="text-gray-700 dark:text-gray-300 font-medium">1. Cover Page</p>
              {sortedDocs.map((doc, index) => {
                  return <p key={doc.id} className="text-gray-700 dark:text-gray-300">{index + 2}. {doc.document.name}</p>;
              })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between pt-8 border-t border-gray-200 dark:border-gray-700 gap-2">
          <motion.button onClick={() => { console.log('Navigating back to arrangement'); onPrevious(); }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="btn btn-outline btn-lg">Back to Arrangement</motion.button>
          <motion.button onClick={handlePreview} disabled={isGenerating || sortedDocs.length === 0} whileHover={!(isGenerating || sortedDocs.length === 0) ? { scale: 1.02 } : {}} whileTap={!(isGenerating || sortedDocs.length === 0) ? { scale: 0.98 } : {}} className={cn('btn btn-outline btn-lg min-w-24', (isGenerating || sortedDocs.length === 0) && 'opacity-50 cursor-not-allowed')}>{generationType === 'preview' ? 'Generating...' : 'Preview Packet'}</motion.button>
          <motion.button onClick={handleGenerate} disabled={isGenerating || sortedDocs.length === 0} whileHover={!(isGenerating || sortedDocs.length === 0) ? { scale: 1.02 } : {}} whileTap={!(isGenerating || sortedDocs.length === 0) ? { scale: 0.98 } : {}} className={cn('btn btn-primary btn-lg min-w-24', (isGenerating || sortedDocs.length === 0) && 'opacity-50 cursor-not-allowed')}>{generationType === 'download' ? 'Downloading...' : 'Download Packet'}</motion.button>
        </div>
        {isPreviewVisible && (
          <div className="fixed inset-0 z-[999] bg-gray-950">
            <div className="absolute inset-0 flex flex-col">
              <div className="p-4 bg-gray-900 text-white flex items-center justify-between">
                <div>
                  <p className="font-semibold">Preview Packet (Read Only)</p>
                  <p className="text-xs text-gray-300">Download & print disabled in this preview</p>
                </div>
                <button className="btn btn-sm btn-outline" onClick={() => setIsPreviewVisible(false)}>Close</button>
              </div>
              <div className="flex-1 flex justify-center overflow-auto p-4">
                <div ref={previewContainerRef} className="space-y-6 max-w-5xl flex flex-col items-center" />
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default PacketGeneration;